import { Type, read, write } from '@acorns/web-utils'
import { withSafeInterval } from '@hocs/safe-timers'
import { compose, lifecycle, withHandlers, withPropsOnChange, withState } from 'recompose'

import { AUTH_TOKEN } from 'app-constants'
import { IErrorDetails } from 'utils/apollo'
import { closeSession, hasToken } from 'utils/auth'
import { ModalActionTypes } from 'utils/prompts'

export const ONE_MINUTE = 60000 // one minute in ms
export const SESSION_LENGTH = 30 // 30 minutes
export const REFRESH_INTERVAL = 10 // 10 minutes
export const PROMPT_REFRESH_AT = 5 // 5 minutes

type Props = {
  shouldReset: boolean
  afterReset: () => void
  modalFactory: (prompt: IErrorDetails) => void
  closeModal: () => void
}

export type SessionRefresherProps = {
  setShouldReset: (shouldReset: boolean) => void
} & Props

type InnerProps = {
  timeLeft: number
  minsSinceLastReset: number
  setTimeLeft: (timeLeft: number) => void
  setMinsSinceLastReset: (minsSinceLastReset: number) => void
  setWarnUser: (warnUser: boolean) => void
  warnUser: boolean
} & Props &
  SafeTimerProps &
  GqlMutators

type SafeTimerProps = {
  setSafeInterval: (fn: () => void, time?: number) => () => () => void
}

type GqlMutators = {
  refreshSession: () => Promise<void>
}

const withSessionTimer = withState('shouldReset', 'setShouldReset', false)

export const sessionAlmostExpiredModal = ({
  closeModal,
  refreshSession,
  setMinsSinceLastReset,
  setTimeLeft,
  setWarnUser,
}) => ({
  title: 'Session Time Out',
  message: 'Your session is about to expire',
  actions: [
    {
      handler: () => {
        closeModal()
        setWarnUser(false)
      },
      label: 'Cancel',
      type: ModalActionTypes.secondary,
    },
    {
      handler: () => {
        refreshSession({}).then(() => {
          setTimeLeft(SESSION_LENGTH)
          setMinsSinceLastReset(0)
        })

        closeModal()
      },
      label: 'Refresh Session',
      type: ModalActionTypes.primary,
    },
  ],
})

export const sessionInactiveModal = {
  title: 'Logged Out',
  message: "You've been logged out due to inactivity.",
  actions: [
    {
      handler: () => window.location.assign('/'),
      label: 'Ok',
      type: ModalActionTypes.primary,
    },
  ],
}

export const triggerRefresh = ({
  afterReset,
  minsSinceLastReset,
  refreshSession,
  setTimeLeft,
  setMinsSinceLastReset,
  shouldReset,
  timeLeft,
}) => {
  // Only update timer if user is signed in.
  if (hasToken()) {
    setTimeLeft(timeLeft - 1)
    setMinsSinceLastReset(minsSinceLastReset + 1)
  }

  if (shouldReset) {
    // Only fire the refresh mutation when exceeding the refresh interval
    if (minsSinceLastReset >= REFRESH_INTERVAL) {
      setTimeLeft(SESSION_LENGTH)
      setMinsSinceLastReset(0)

      refreshSession({}).then(afterReset)
    } else {
      afterReset()
    }
  }
}

const updateTimeEveryMinute = lifecycle({
  componentDidMount() {
    const { setSafeInterval } = this.props as InnerProps

    setSafeInterval(() => {
      triggerRefresh(this.props as InnerProps)
    }, ONE_MINUTE)
  },
})

const promptUserIfSessionAlmostExpired = withPropsOnChange<InnerProps, InnerProps>(
  ['timeLeft'],
  props => {
    const { modalFactory, timeLeft, warnUser } = props

    if (warnUser && timeLeft <= PROMPT_REFRESH_AT && timeLeft > 1) {
      modalFactory(sessionAlmostExpiredModal(props))
    }

    return props
  },
)

const promptUserIfSessionExpired = withPropsOnChange<InnerProps, InnerProps>(
  ['timeLeft'],
  props => {
    if (props.timeLeft <= 0) {
      props.closeModal()

      closeSession()

      props.modalFactory(sessionInactiveModal)
    }

    return props
  },
)

export const enhance = compose<InnerProps, Props>(
  withSafeInterval,
  withHandlers({
    refreshSession: () => () => {
      const token = read(Type.token)

      if (token) {
        write(Type.token, token)
        write(Type.partition, read(Type.partition))

        // Safari does not persist cookies between redirects.
        window.sessionStorage.setItem(AUTH_TOKEN, token)
      }

      return Promise.resolve()
    },
  }),
  withState('timeLeft', 'setTimeLeft', SESSION_LENGTH),
  withState('minsSinceLastReset', 'setMinsSinceLastReset', 0),
  withState('warnUser', 'setWarnUser', true),
  updateTimeEveryMinute,
  promptUserIfSessionAlmostExpired,
  promptUserIfSessionExpired,
)

const SessionTimer = enhance(() => null)

export { SessionTimer, withSessionTimer }
